/**
 * Scene graph. Owns the SVG, builds it from a bundle + view state, and applies
 * the two independent transforms (camera, ring rotation) without re-rendering.
 *
 *   <svg>
 *     <g id="camera">                       translate(tx,ty) scale(scale)
 *       <g id="ring-layer">                 rotate(rotation)
 *         <g data-ring="root">              translate(cx,cy)
 *           <g class="ring-shape">          ring / band / month / day
 *           <g class="ring-marks">          ringMark
 *           <g class="ring-top">            today
 *         <g data-ring="tag_work">          …one of these per ring
 *       <g id="float-layer">                world coords, unrotated
 *         <g id="leader-layer">             rotate(rotation) — leaders are geometry
 *         <g id="float-slots">              one slot per placed float or
 *                                           cluster, at the rotated world
 *                                           point, so the card stays upright
 *                                           and is drawn in screen pixels
 *
 * A calendar is a *tree* of circles now (docs/CIRCLES.md §2). `layoutRings`
 * says where each one goes; this file keeps a registry of what came back, gives
 * every ring its own group and its own ring/month/day/today fragments at its
 * own radius, and draws each event on every ring it belongs to. A calendar with
 * no circles lays out as one ring at the origin and renders exactly what it
 * always did, down to the entry ids.
 *
 * The ring layer's rotation stays a single transform on `#ring-layer`: rings
 * carry their own translate so a later phase can spin one independently, but
 * today they all turn together.
 *
 * Floats are positioned by `layoutFloats` (pure, in @circle/shared) through
 * `layoutPass()`, once per *float ring* — a distinct (centre, radius) that
 * events sit on, which is what keeps two bands of one merged circle from
 * packing their cards through each other. Angles handed to it are world-frame
 * and rotation-free; the rotation is applied here when a slot is placed, which
 * is why spinning the ring never needs a re-pack.
 *
 * A slot is also the card's keyboard and screen-reader identity: it is
 * focusable, a button, and labelled with the entry it carries, so the ring is
 * reachable without a pointer. Beside the `<svg>` the scene keeps a hidden
 * list of every entry in date order for a reader that would rather read than
 * look; `InputController` owns the keys.
 */
import {
  MONTH_NAMES,
  RING_RADIUS,
  clusterLabel,
  dateToAngle,
  daysInMonth,
  effectiveMonthDay,
  floatAngularExtent,
  floatWorldSize,
  layoutFloats,
  monthArc,
  opacityFloor,
  polarToXY,
  readsMirrored,
  resolveStyle,
  ringTransitionPlan,
  rotatePoint,
  transformOf,
  xyToPolar,
  type CalEvent,
  type CalendarBundle,
  type FloatPin,
  type LayoutItem,
  type Placed,
  type PlacedCluster,
  type PlacedFloat,
  type Point,
  type ResolvedStyle,
  type RingBand,
  type RingMode,
  type RingNode,
  type RingPlacement,
  type RingTransform,
  type Source,
  type Tag,
  type ViewLayout,
  type ViewState,
} from '@circle/shared';
import { n, setAttrs, svgEl } from './svg';
import { ageText, detailText, isWeekend } from './format';
import { arcDrift, visibleArc, type Arc } from './cull';
import {
  DEFAULT_VIEW_LAYOUT,
  ROOT_RING_ID,
  bandAnnulus,
  bandEventRadius,
  bandWeights,
  boundingRadius,
  eventStyleLevel,
  floatIdFor,
  indexBy,
  layoutFor,
  markIdFor,
  onRing,
  ringsForEvent,
  sourceStyleLevel,
  tagPathOf,
  tagStyleLevel,
} from './rings';
import { RingTweener, type RingTweenerOptions } from './rings-anim';
import {
  EMPTY_STATE,
  floatSizing,
  getTheme,
  styleDefaults,
  themeTokenOverrides,
  type BandCtx,
  type ClusterCtx,
  type DayCtx,
  type FloatCtx,
  type FragState,
  type FragmentKind,
  type LeaderCtx,
  type MonthCtx,
  type RingCtx,
  type RingMarkCtx,
  type Theme,
  type TodayCtx,
} from '../fragments/types';
import { markSizeFactor } from '../fragments/default/mark';
import '../fragments/default';

/**
 * How far a float may slide from its date before its run clusters, measured in
 * its own angular width. Screen-constant floats subtend a smaller angle the
 * further you zoom in, so a fixed radian budget would be generous at 10x and
 * mean at fit; tying it to the float's own extent keeps the feel constant. At
 * fit zoom a card spans ~0.44 rad, so this lands on the ~0.35 rad that reads
 * right: a float may drift about three quarters of its own width off its date.
 */
export const MAX_SLIDE_FLOAT_WIDTHS = 0.8;

/**
 * ...but never less than four days of ring. Purely proportional slide vanishes
 * as you zoom in — extents shrink, so the budget shrinks with them — and a tight
 * run like the four March birthdays in the sample data would then never resolve
 * short of absurd zoom. A floor of a few days is a promise the reader can hold:
 * a float is never more than about four days off its own date.
 */
export const MIN_SLIDE_RAD = (Math.PI * 2 * 4) / 365;

/** Re-pack only past this much zoom change, so clusters cannot flicker. */
export const LAYOUT_SCALE_HYSTERESIS = 0.05;
/** ...or once the visible arc has drifted this far (radians), while culling. */
export const LAYOUT_ARC_HYSTERESIS = 0.06;

/**
 * Camera scale, as a multiple of fit, below which a merged circle's tree rings
 * collapse into one band (docs/CIRCLES.md §2, the D3-a fallback). Pulled back
 * this far the bands are a few pixels apart and read as noise on the ring
 * rather than as structure, so the ring goes back to being one ring and every
 * event sits on it. `ringBand` leaves this call to the scene because only the
 * scene knows the camera.
 */
export const MERGED_BANDS_MIN_SCALE = 0.6;

/**
 * Below this share of the root radius a ring stops drawing all 365 day ticks
 * and every month name: at a quarter of the ring the ticks are closer together
 * than a hairline is wide, and the labels are taller than the gutter they sit
 * in. Weekly ticks plus the month boundaries keep the shape of the year.
 */
export const SMALL_RING_FRACTION = 0.5;

/** How many days apart the ticks are on a ring too small for all of them. */
const SPARSE_DAY_STEP = 7;

/** Avatars a cluster card shows before it falls back to "+N". */
const CLUSTER_AVATARS = 3;

/** Padding round the fitted ring, in CSS pixels. `computeFit` and the band threshold agree on it here. */
export const FIT_PADDING = 24;
/** Extra room around the ring at "fit", so the label band and floats show. */
export const FIT_HEADROOM = 1.32;

interface SceneEntryBase {
  id: string;
  state: FragState;
  el: SVGElement;
  /** Group the fragment element lives in (a per-float slot, or a layer group). */
  parent: SVGGElement;
  /** The single event this fragment stands for, when there is one. */
  eventId?: string;
  /** Every event this fragment stands for: one for a float, many for a cluster. */
  eventIds?: string[];
  /** The ring this fragment is drawn on: `'root'` or a tag id. */
  ringId?: string;
  /** Float and cluster slots: unrotated world position, used to place the slot. */
  world?: Point;
  /** Cheap identity of the drawn content; a change forces a rebuild. */
  sig?: string;
}

type Entry<K extends FragmentKind, C> = SceneEntryBase & {
  kind: K;
  /** What the fragment was drawn from. */
  ctx: C;
};

/**
 * One drawn fragment. `kind` decides what `ctx` is, so a check on the one
 * narrows the other: `if (entry.kind === 'ringMark') entry.ctx.angle`.
 */
export type SceneEntry =
  | Entry<'ring', RingCtx>
  | Entry<'band', BandCtx>
  | Entry<'month', MonthCtx>
  | Entry<'day', DayCtx>
  | Entry<'ringMark', RingMarkCtx>
  | Entry<'leader', LeaderCtx>
  | Entry<'float', FloatCtx>
  | Entry<'cluster', ClusterCtx>
  | Entry<'today', TodayCtx>;

export type SceneEntryOf<K extends FragmentKind> = Extract<SceneEntry, { kind: K }>;
export type CtxOf<K extends FragmentKind> = SceneEntryOf<K>['ctx'];

/** A predicate for one kind, for `find` and `filter` over `scene.all()`. */
export function ofKind<K extends FragmentKind>(kind: K): (entry: SceneEntry) => entry is SceneEntryOf<K> {
  return (entry): entry is SceneEntryOf<K> => entry.kind === kind;
}

export interface SceneBundle {
  calendar: { theme: { name: string; tokens?: Record<string, string> } };
  sources: Source[];
  events: CalEvent[];
  tags?: Tag[];
}

/** One ring's group and everything drawn into it. */
interface RingGroup {
  group: SVGGElement;
  shape: SVGGElement;
  marks: SVGGElement;
  top: SVGGElement;
  layers: Record<'ring' | 'band' | 'month' | 'day' | 'ringMark' | 'today', SVGGElement>;
}

/**
 * A node of the tree, placed. A node whose parent is `merged` is a *band*: it
 * has no ring of its own, borrows its parent's group and transform, and its
 * events sit in the middle of the annulus `bandAnnulus` gave it — which for a
 * band of a band is carved out of its parent's annulus, not out of the ring.
 */
export interface RingSlot {
  id: string;
  placement: RingPlacement;
  node: RingNode;
  tag: Tag | undefined;
  tagPath: Tag[];
  /** The group this node's fragments are drawn into — its own, or its parent's. */
  ring: RingGroup;
  /** True when this node owns `ring` rather than borrowing it. */
  ownsRing: boolean;
  /** Absolute world centre. */
  cx: number;
  cy: number;
  /** The radius the ring itself is drawn at. */
  r: number;
  /** The radius this node's *events* sit on: the band's middle, or `r`. */
  eventRadius: number;
  band: RingBand | undefined;
  style: ResolvedStyle;
}

/** What the UI needs to know about a ring without reaching into the scene. */
export interface RingInfo {
  id: string;
  parentId: string | null;
  tagId: string | null;
  name: string;
  cx: number;
  cy: number;
  r: number;
  eventRadius: number;
  depth: number;
  mode: RingMode;
  visible: boolean;
  ghost: boolean;
  bandIndex: number | undefined;
  bandCount: number | undefined;
  colour: string;
}

export interface SceneOptions {
  /** Passed straight to the ring tweener; tests use it to drive the clock. */
  tween?: RingTweenerOptions;
}

const DEG = 180 / Math.PI;

export class Scene {
  readonly svg: SVGSVGElement;
  private readonly camera: SVGGElement;
  private readonly ringLayer: SVGGElement;
  private readonly floatLayer: SVGGElement;
  private readonly leaderLayer: SVGGElement;
  private readonly floatSlots: SVGGElement;

  /** The hidden, readable form of the ring: one item per visible entry, in date order. */
  readonly entriesList: HTMLUListElement;

  private theme: Theme = getTheme();
  /** The calendar's own colours over the theme's, already validated. */
  private tokenOverrides: Record<string, string> = {};
  private entries = new Map<string, SceneEntry>();
  private view: ViewState = { year: new Date().getFullYear(), rotation: 0, scale: 1, tx: 0, ty: 0 };
  private bundle: SceneBundle | undefined;
  private today = new Date();

  private viewport = { width: 1, height: 1 };
  private visible: CalEvent[] = [];
  private visibleById = new Map<string, CalEvent>();
  private sources = new Map<string, Source>();
  private tagsById = new Map<string, Tag>();
  private pins = new Map<string, FloatPin | null>();
  private livePin: { eventId: string; ringId: string; pin: FloatPin } | undefined;
  private filter: ((event: CalEvent) => boolean) | null = null;

  // --- the ring registry ---
  private layout: ViewLayout = DEFAULT_VIEW_LAYOUT;
  private ringNodes: RingNode[] = [];
  private nodesById = new Map<string, RingNode>();
  private ringPlacements: RingPlacement[] = [];
  private placementById = new Map<string, RingPlacement>();
  private slots = new Map<string, RingSlot>();
  private bandsCollapsed = false;
  /** eventId → the ring ids it is drawn on, in placement order. */
  private eventRings = new Map<string, string[]>();

  private readonly tweener: RingTweener;
  private tweening = false;

  private placements = new Map<string, Placed>();
  /** eventId → the entry id of the float or cluster currently showing it. */
  private ownerOf = new Map<string, string>();
  /** ...and every one of them, when the event is on more than one ring. */
  private ownersOfEvent = new Map<string, string[]>();
  /** State of entries that layout has taken away, so selection survives a re-pack. */
  private stashed = new Map<string, FragState>();

  private layoutScale = 0;
  private layoutArc: Arc | null = null;
  private layoutPending = 0;

  /**
   * Fired after a re-render or a layout pass that changed what is on screen,
   * with the ids it took away. The pointer has not moved, so nothing else would
   * notice that what it was over — a cluster that has just dissolved, an entry a
   * reload replaced — is gone or is now something else. `InputController` uses
   * it to drop a hover, and with it a stale tooltip.
   */
  onEntriesChanged: ((removed: string[]) => void) | undefined;

  constructor(host: HTMLElement, options: SceneOptions = {}) {
    this.svg = svgEl('svg', {
      class: 'circle-viewport',
      xmlns: 'http://www.w3.org/2000/svg',
    });

    this.camera = svgEl('g', { id: 'camera' });
    this.ringLayer = svgEl('g', { id: 'ring-layer' });
    this.floatLayer = svgEl('g', { id: 'float-layer' });
    this.leaderLayer = svgEl('g', { id: 'leader-layer' });
    this.floatSlots = svgEl('g', { id: 'float-slots' });

    this.floatLayer.append(this.leaderLayer, this.floatSlots);
    this.camera.append(this.ringLayer, this.floatLayer);
    this.svg.append(this.camera);
    host.append(this.svg);

    this.entriesList = document.createElement('ul');
    this.entriesList.className = 'sr-only';
    this.entriesList.setAttribute('aria-label', 'Entries');
    host.append(this.entriesList);

    this.tweener = new RingTweener(options.tween);
    this.applyTokens();
  }

  /** Take the scene, and everything it put in the host, out of the document. */
  destroy(): void {
    this.tweener.stop();
    this.svg.remove();
    this.entriesList.remove();
  }

  /**
   * How much world a layout of `bundle` covers, without drawing any of it.
   *
   * A page that has to point the camera before its first render used to render
   * once to find out — a year of ticks built twice on every open. The bounds
   * follow from `layoutRings` alone.
   */
  static measure(bundle: Pick<SceneBundle, 'tags'>, layout: ViewLayout = DEFAULT_VIEW_LAYOUT): number {
    return boundingRadius(layoutFor(bundle.tags ?? [], layout, RING_RADIUS).placements, RING_RADIUS);
  }

  // --- rendering ------------------------------------------------------------

  setTheme(name: string | undefined): void {
    this.theme = getTheme(name);
    this.applyTokens();
    if (this.bundle) this.render(this.bundle, this.view);
  }

  /**
   * The theme's tokens with the calendar's overrides on top. Every theme token
   * is written each time, so an override that has gone falls back to the
   * theme's value rather than lingering from the last calendar drawn.
   */
  private applyTokens(): void {
    for (const [key, value] of Object.entries({ ...this.theme.tokens, ...this.tokenOverrides })) {
      this.svg.style.setProperty(key, value);
      document.documentElement.style.setProperty(key, value);
    }
  }

  /** Full rebuild. Cheap enough for a year of ticks; not called per frame. */
  render(bundle: CalendarBundle | SceneBundle, view: ViewState, today = new Date()): void {
    this.bundle = bundle;
    this.view = view;
    this.today = today;

    const themeName = bundle.calendar.theme?.name;
    const overrides = themeTokenOverrides(bundle.calendar.theme?.tokens);
    const overridesChanged = !sameRecord(overrides, this.tokenOverrides);
    if ((themeName && themeName !== this.theme.name) || overridesChanged) {
      this.theme = getTheme(themeName);
      this.tokenOverrides = overrides;
      this.applyTokens();
    }

    const previous = this.entries;
    this.entries = new Map();
    this.placements.clear();
    this.ownerOf.clear();
    this.ownersOfEvent.clear();
    this.leaderLayer.replaceChildren();
    this.floatSlots.replaceChildren();

    const keepState = (id: string): FragState =>
      previous.get(id)?.state ?? this.stashed.get(id) ?? { ...EMPTY_STATE };
    for (const [id, entry] of previous) {
      if ((entry.kind === 'float' || entry.kind === 'cluster') && (entry.state.selected || entry.state.hover)) {
        this.stashed.set(id, entry.state);
      }
    }

    this.sources = new Map(bundle.sources.map((s) => [s.id, s]));
    this.tagsById = indexBy(bundle.tags ?? []);
    this.visible = visibleEvents(bundle.events, view.year);
    this.visibleById = new Map(this.visible.map((e) => [e.id, e]));

    this.buildRings();
    this.renderRings(keepState);
    this.renderMarks(keepState);
    this.renderEntriesList();

    this.layoutPass(true);

    // Always, not only when something vanished: a full render redraws every
    // fragment's contents, so even a surviving id may now say something else.
    this.onEntriesChanged?.([...previous.keys()].filter((id) => !this.entries.has(id)));
  }

  /**
   * Show only the entries a predicate keeps; `null` shows every one again.
   *
   * Hiding is the wrong word for it. An entry the filter turns down keeps its
   * mark on the ring — dimmed, so the year still reads as a year and a match
   * can be seen among its neighbours — and loses only its **card**: it leaves
   * the float layout entirely, which is what gives the matches the room. That
   * is why this is a filter on the packer rather than on `visibleEvents`: the
   * shape of the year must not change because somebody typed a letter.
   *
   * It survives a re-render, so a reload or a year change keeps whatever the
   * search field says, and the hidden entries list is filtered with it — a
   * screen reader hears the same ring everyone else is looking at.
   */
  setFilter(keep: ((event: CalEvent) => boolean) | null): void {
    this.filter = keep;
    for (const entry of this.entries.values()) {
      if (entry.kind !== 'ringMark') continue;
      this.setState(entry.id, { dimmed: !this.matchesFilter(entry.ctx.event) });
    }
    this.renderEntriesList();
    this.layoutPass(true);
  }

  /** The filter in play, or `null` when everything is shown. */
  currentFilter(): ((event: CalEvent) => boolean) | null {
    return this.filter;
  }

  private matchesFilter(event: CalEvent): boolean {
    return this.filter === null || this.filter(event);
  }

  /** Viewport size in CSS pixels; culling needs it. */
  resize(width: number, height: number): void {
    this.viewport = { width, height };
    this.svg.setAttribute('width', String(width));
    this.svg.setAttribute('height', String(height));
    this.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }

  // --- the ring registry ----------------------------------------------------

  /** The layout the rings are currently drawn from. */
  viewLayout(): ViewLayout {
    return this.layout;
  }

  /** Every ring on screen, root first, in layout order. */
  rings(): RingInfo[] {
    return this.ringPlacements.map((p) => {
      const slot = this.slots.get(p.id);
      const tag = this.tagsById.get(p.id);
      return {
        id: p.id,
        parentId: p.parentId,
        tagId: p.id === ROOT_RING_ID ? null : p.id,
        name: tag?.name ?? 'Everyone',
        cx: p.cx,
        cy: p.cy,
        r: p.r,
        eventRadius: slot?.eventRadius ?? p.r,
        depth: p.depth,
        mode: this.nodesById.get(p.id)?.mode ?? 'merged',
        visible: this.nodesById.get(p.id)?.visible !== false,
        ghost: Boolean(p.ghost),
        bandIndex: p.bandIndex,
        bandCount: p.bandCount,
        colour: slot?.style.colour ?? '',
      };
    });
  }

  ring(id: string): RingInfo | undefined {
    return this.rings().find((r) => r.id === id);
  }

  /**
   * Where a ring sits, cheaply — for callers turning ring-local geometry into
   * world coordinates in a loop, where `rings()` would rebuild the whole list.
   */
  ringTransform(id: string): { cx: number; cy: number; r: number; eventRadius: number } | undefined {
    const slot = this.slots.get(id);
    if (!slot) return undefined;
    return { cx: slot.cx, cy: slot.cy, r: slot.r, eventRadius: slot.eventRadius };
  }

  /** The world radius the camera has to cover to hold every ring. */
  get boundsRadius(): number {
    return boundingRadius(this.ringPlacements, RING_RADIUS);
  }

  /**
   * Re-lay the tree out. With `animate` the rings tween to their new places
   * over half a second (docs/CIRCLES.md §3): floats are hidden for the duration
   * and re-packed on settle, ring/month/day fragments are re-rendered once at
   * the end rather than 30 times on the way, and the marks are nudged per frame
   * so an event never leaves its date. `prefers-reduced-motion` jump-cuts.
   */
  setViewLayout(layout: ViewLayout, opts: { animate?: boolean } = {}): void {
    const before = this.ringPlacements;
    this.layout = layout ?? DEFAULT_VIEW_LAYOUT;
    if (!this.bundle) return;

    if (!opts.animate || before.length === 0) {
      this.tweener.stop();
      this.tweening = false;
      this.render(this.bundle, this.view, this.today);
      return;
    }

    this.render(this.bundle, this.view, this.today);
    const plan = ringTransitionPlan(before, this.ringPlacements);
    // A ring that is leaving has no group any more; it is in the plan so the
    // geometry stays honest, and simply has nothing to write to.
    this.tweening = true;
    this.floatLayer.setAttribute('visibility', 'hidden');
    this.tweener.run(plan, {
      onFrame: (at) => this.applyRingTransforms(at),
      onSettle: () => {
        this.tweening = false;
        this.floatLayer.removeAttribute('visibility');
        this.applyRingTransforms(new Map(this.ringPlacements.map((p) => [p.id, transformOf(p)])));
        this.layoutPass(true);
      },
    });
  }

  /** Stop a running tween and land on the final layout at once. */
  settleRings(): void {
    this.tweener.cancel();
  }

  get isTweening(): boolean {
    return this.tweening;
  }

  /**
   * One transform write per ring per frame, plus one per mark.
   *
   * The ring's structural fragments were built at its *final* radius, so a
   * scale on the two groups that hold them is the whole of the radius tween —
   * no re-rendering, no per-tick arithmetic. Marks are deliberately left out of
   * that scale (they are drawn in screen pixels and would swell), and are moved
   * instead: the offset from where a date sits at the final radius to where it
   * sits at this frame's radius, which is a translate along the mark's own ray.
   */
  private applyRingTransforms(at: ReadonlyMap<string, RingTransform>): void {
    for (const slot of this.slots.values()) {
      if (!slot.ownsRing) continue;
      const now = at.get(slot.id);
      if (!now) continue;
      slot.ring.group.setAttribute('transform', `translate(${n(now.cx)} ${n(now.cy)})`);
      const k = slot.r > 0 ? now.r / slot.r : 1;
      setRingScale(slot.ring.shape, k);
      setRingScale(slot.ring.top, k);
    }

    for (const entry of this.entries.values()) {
      if (entry.kind !== 'ringMark') continue;
      const slot = entry.ringId ? this.slots.get(entry.ringId) : undefined;
      if (!slot) continue;
      const now = at.get(slot.id);
      if (!now) continue;
      const k = slot.r > 0 ? now.r / slot.r : 1;
      const { angle, radius } = entry.ctx;
      const from = polarToXY(angle, radius);
      const to = polarToXY(angle, radius * k);
      if (k === 1) entry.el.removeAttribute('transform');
      else entry.el.setAttribute('transform', `translate(${n(to.x - from.x)} ${n(to.y - from.y)})`);
    }
  }

  /**
   * Is the camera pulled back far enough that tree rings stop reading? The
   * threshold is a multiple of *fit* rather than an absolute scale, so it means
   * the same thing on a phone and on a desk.
   */
  private bandsShouldCollapse(): boolean {
    const usable = Math.max(1, Math.min(this.viewport.width, this.viewport.height) - 2 * FIT_PADDING);
    const fit = usable / (2 * this.boundsRadius) / FIT_HEADROOM;
    return this.view.scale > 0 && fit > 0 && this.view.scale < fit * MERGED_BANDS_MIN_SCALE;
  }

  /**
   * Build the registry and the groups. Root first, depth first, which is the
   * order `layoutRings` returns and the order that guarantees a band's parent
   * already has the group the band is about to borrow.
   */
  private buildRings(): void {
    const tags = this.bundle?.tags ?? [];
    this.bandsCollapsed = this.bandsShouldCollapse();
    const laid = layoutFor(tags, this.layout, RING_RADIUS, this.bandsCollapsed);
    this.ringNodes = laid.nodes;
    this.nodesById = indexBy(laid.nodes);
    this.ringPlacements = laid.placements;
    this.placementById = new Map(laid.placements.map((p) => [p.id, p]));

    this.ringLayer.replaceChildren();
    this.slots.clear();

    for (const placement of this.ringPlacements) {
      const node = this.nodesById.get(placement.id);
      if (!node) continue;
      const isBand = placement.bandIndex !== undefined;
      const parentSlot = placement.parentId ? this.slots.get(placement.parentId) : undefined;
      const ring = isBand && parentSlot ? parentSlot.ring : this.makeRingGroup(placement);
      const tag = this.tagsById.get(placement.id);
      const tagPath = tagPathOf(placement.id, this.tagsById);

      let band: RingBand | undefined;
      let eventRadius = placement.r;
      if (isBand) {
        const count = placement.bandCount ?? 1;
        // The placement carries the whole chain of bands it hangs off, so a
        // band of a band lands inside its parent's tree ring rather than on the
        // ring line they all share.
        band = bandAnnulus(
          placement,
          placement.bandPath ?? [
            {
              index: placement.bandIndex ?? 0,
              count,
              weights: bandWeights(this.ringPlacements, placement.parentId, this.nodesById),
            },
          ],
        );
        eventRadius = bandEventRadius(band, count, placement.r);
      }

      this.slots.set(placement.id, {
        id: placement.id,
        placement,
        node,
        tag,
        tagPath,
        ring,
        ownsRing: !(isBand && parentSlot),
        cx: placement.cx,
        cy: placement.cy,
        r: placement.r,
        eventRadius,
        band,
        style: this.resolveFor(tagPath, undefined, undefined),
      });
    }
  }

  private makeRingGroup(placement: RingPlacement): RingGroup {
    const isRoot = placement.id === ROOT_RING_ID;
    const group = svgEl('g', {
      'data-ring': placement.id,
      transform: `translate(${n(placement.cx)} ${n(placement.cy)})`,
    });
    const shape = svgEl('g', { class: 'ring-shape' });
    const marks = svgEl('g', { class: 'ring-marks' });
    const top = svgEl('g', { class: 'ring-top' });

    // The root ring keeps the layer ids the rest of the app has always used;
    // every other ring gets the class only, because an id may appear once.
    const layer = (name: string): SVGGElement =>
      svgEl('g', { class: `layer-${name}`, ...(isRoot ? { id: `layer-${name}` } : {}) });

    const layers = {
      ring: layer('ring'),
      band: layer('band'),
      month: layer('month'),
      day: layer('day'),
      ringMark: layer('ringMark'),
      today: layer('today'),
    };

    // Back to front, per SPEC.
    shape.append(layers.ring, layers.band, layers.month, layers.day);
    marks.append(layers.ringMark);
    top.append(layers.today);
    group.append(shape, marks, top);
    this.ringLayer.append(group);
    return { group, shape, marks, top, layers };
  }

  /** Ring, bands, months, days and the today hand — once per ring. */
  private renderRings(keepState: (id: string) => FragState): void {
    const year = this.view.year;
    const todayKey =
      this.today.getFullYear() === year
        ? `day:${this.today.getMonth() + 1}:${this.today.getDate()}`
        : undefined;

    for (const slot of this.slots.values()) {
      if (slot.ownsRing) this.renderOneRing(slot, keepState, year, todayKey);
      else this.renderBand(slot, keepState);
    }
  }

  private renderOneRing(
    slot: RingSlot,
    keepState: (id: string) => FragState,
    year: number,
    todayKey: string | undefined,
  ): void {
    const small = slot.r < SMALL_RING_FRACTION * RING_RADIUS;
    const base = { ringId: slot.id, radius: slot.r };

    const ringId = onRing('ring', slot.id);
    this.add(
      'ring',
      ringId,
      {
        ...base,
        year,
        tag: slot.tag,
        style: slot.style,
        ghost: Boolean(slot.placement.ghost),
      },
      keepState(ringId),
      slot.ring.layers.ring,
      undefined,
      slot.id,
    );

    for (let m = 1; m <= 12; m++) {
      const arc = monthArc(year, m);
      const id = onRing(`month:${m}`, slot.id);
      this.add(
        'month',
        id,
        {
          ...base,
          index: m,
          name: MONTH_NAMES[m - 1]!,
          startAngle: arc.start,
          endAngle: arc.end,
          year,
          rotation: this.view.rotation,
          ...(small ? { compact: true } : {}),
        },
        keepState(id),
        slot.ring.layers.month,
        undefined,
        slot.id,
      );
    }

    for (let m = 1; m <= 12; m++) {
      for (let d = 1; d <= daysInMonth(year, m); d++) {
        // A small ring keeps the month boundaries and a tick a week; the rest
        // would be closer together than the hairline drawing them.
        if (small && d !== 1 && (d - 1) % SPARSE_DAY_STEP !== 0) continue;
        const key = `day:${m}:${d}`;
        const id = onRing(key, slot.id);
        this.add(
          'day',
          id,
          {
            ...base,
            year,
            month: m,
            day: d,
            angle: dateToAngle(year, m, d),
            isToday: key === todayKey,
            isWeekend: isWeekend(year, m, d),
          },
          keepState(id),
          slot.ring.layers.day,
          undefined,
          slot.id,
        );
      }
    }

    if (todayKey) {
      const angle = dateToAngle(year, this.today.getMonth() + 1, this.today.getDate());
      const id = onRing('today', slot.id);
      this.add(
        'today',
        id,
        { ...base, angle },
        keepState(id),
        slot.ring.layers.today,
        undefined,
        slot.id,
      );
    }
  }

  private renderBand(slot: RingSlot, keepState: (id: string) => FragState): void {
    const band = slot.band;
    if (!band) return;
    const id = `band:${slot.id}`;
    this.add(
      'band',
      id,
      {
        ringId: slot.placement.parentId ?? ROOT_RING_ID,
        radius: slot.r,
        bandIndex: slot.placement.bandIndex ?? 0,
        bandCount: slot.placement.bandCount ?? 1,
        rInner: band.rInner,
        rOuter: band.rOuter,
        tag: slot.tag,
        style: slot.style,
      },
      keepState(id),
      slot.ring.layers.band,
      undefined,
      slot.id,
    );
  }

  /**
   * A mark per (event, ring). On a merged ring an event is drawn once, in its
   * loudest circle, and carries a dot for each circle it is also in; on split
   * and concentric rings it is drawn on every one of them (D2-a).
   */
  private renderMarks(keepState: (id: string) => FragState): void {
    const screenScale = 1 / (this.view.scale > 0 ? this.view.scale : 1);
    this.eventRings.clear();

    for (const event of this.visible) {
      const { rings, merged, others } = ringsForEvent(
        event,
        this.ringPlacements,
        this.placementById,
        this.nodesById,
      );
      if (rings.length === 0) continue;
      this.eventRings.set(event.id, rings);

      const memberColours = merged
        ? others
            .map((tagId) => this.tagsById.get(tagId)?.colour)
            .filter((c): c is string => Boolean(c))
        : [];

      for (const ringId of rings) {
        const slot = this.slots.get(ringId);
        if (!slot) continue;
        const style = this.styleFor(event, slot);
        const id = markIdFor(event.id, ringId);
        this.add(
          'ringMark',
          id,
          {
            event,
            angle: this.angleOf(event),
            ringId,
            radius: slot.eventRadius,
            style,
            colour: style.colour || undefined,
            source: this.sources.get(event.sourceId),
            screenScale,
            ...(memberColours.length ? { memberColours } : {}),
          },
          { ...keepState(id), dimmed: !this.matchesFilter(event) },
          slot.ring.layers.ringMark,
          event.id,
          ringId,
        );
      }
    }
  }

  private angleOf(event: CalEvent): number {
    const eff = effectiveMonthDay(this.view.year, event.month, event.day);
    return dateToAngle(this.view.year, eff.month, eff.day);
  }

  /**
   * The cascade, collapsed (docs/CIRCLES.md §5). `defaults → theme → calendar →
   * tag path → source → event`, with the ad-hoc `event.style.colour ?? source
   * .colour` it replaces falling out of it exactly: an untagged event on a
   * source with a colour still resolves to that colour, and an event style
   * still beats it.
   */
  private resolveFor(
    tagPath: readonly Tag[],
    source: Source | undefined,
    event: CalEvent | undefined,
  ): ResolvedStyle {
    const path = tagPath.map(tagStyleLevel);
    return resolveStyle({
      defaults: styleDefaults(this.theme),
      ...(this.theme.style ? { theme: this.theme.style } : {}),
      tagPath: path,
      source: sourceStyleLevel(source),
      ...(event ? { event: eventStyleLevel(event) } : {}),
    });
  }

  private styleFor(event: CalEvent, slot: RingSlot): ResolvedStyle {
    return this.resolveFor(slot.tagPath, this.sources.get(event.sourceId), event);
  }

  private add<K extends FragmentKind>(
    kind: K,
    id: string,
    ctx: CtxOf<K>,
    state: FragState,
    parent: SVGGElement,
    eventId?: string,
    ringId?: string,
    world?: Point,
    eventIds?: string[],
    sig?: string,
  ): SceneEntryOf<K> {
    // `kind` and `ctx` arrive matched by the signature; the union cannot see
    // that through a type parameter, so this is the one place it is asserted.
    const entry = {
      id,
      kind,
      ctx,
      state,
      parent,
      el: svgEl('g'),
      eventId,
      ringId,
      world,
      eventIds,
      sig,
    } as unknown as SceneEntryOf<K>;
    entry.el = this.build(entry);
    parent.append(entry.el);
    this.entries.set(id, entry);
    return entry;
  }

  private build(entry: SceneEntry): SVGElement {
    const fragment = this.resolveFragment(entry);
    const el = fragment(entry.ctx, entry.state) as SVGElement;
    el.classList.add('frag');
    setAttrs(el, {
      'data-kind': entry.kind,
      'data-id': entry.id,
      'data-event': entry.eventId,
      'data-ring': entry.ringId,
      'data-hover': entry.state.hover ? 'true' : undefined,
      'data-selected': entry.state.selected ? 'true' : undefined,
      'data-dimmed': entry.state.dimmed ? 'true' : undefined,
      'data-pinned': entry.state.pinned ? 'true' : undefined,
    });
    // Prominence recedes rather than vanishes: the opacity floor is 1 at the
    // default weight, so an ordinary calendar never gets an opacity at all.
    const opacity = entryOpacity(entry);
    if (opacity !== undefined) el.setAttribute('opacity', opacity);
    return el;
  }

  /** Theme fragment, or the variant the cascade named in `ringMark` / `float`. */
  private resolveFragment(entry: SceneEntry): (ctx: unknown, state: FragState) => SVGElement {
    if (entry.kind === 'ringMark' && entry.ctx.style?.ringMark) {
      const variant = this.theme.variants?.ringMark?.[entry.ctx.style.ringMark];
      if (variant) return variant as (ctx: unknown, state: FragState) => SVGElement;
    }
    if (entry.kind === 'float' && entry.ctx.style?.float) {
      const variant = this.theme.variants?.float?.[entry.ctx.style.float];
      if (variant) return variant as (ctx: unknown, state: FragState) => SVGElement;
    }
    return this.theme.fragments[entry.kind] as (ctx: unknown, state: FragState) => SVGElement;
  }

  /**
   * What a screen reader is told a card is: the name and the same line the
   * tooltip and the detail panel use, so the ring says one thing everywhere.
   */
  private labelFor(event: CalEvent): string {
    return `${event.title}, ${detailText(event, this.view.year, this.today)}`;
  }

  /** Every visible entry, earliest in the year first; ties by title, then id. */
  entriesByDate(): CalEvent[] {
    return [...this.visible].sort(
      (a, b) =>
        this.angleOf(a) - this.angleOf(b) ||
        a.title.localeCompare(b.title) ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
    );
  }

  /** The hidden list beside the `<svg>`: the ring, read out in date order. */
  private renderEntriesList(): void {
    const items = this.entriesByDate()
      .filter((event) => this.matchesFilter(event))
      .map((event) => {
        const li = document.createElement('li');
        li.dataset.event = event.id;
        li.textContent = this.labelFor(event);
        return li;
      });
    this.entriesList.replaceChildren(...items);
  }

  // --- float layout ---------------------------------------------------------

  /**
   * Which ring a pin made on `ringId` belongs to.
   *
   * A pin is per ring *placement*, not per circle. A merged child is a band on
   * its parent's ring — same centre, same line, no ring of its own — so a card
   * hanging off it is a card on the parent's ring and pins there. Reading a
   * circle's own pin back on a band was the bug: pin a card on Family while
   * Family had a ring out on an orbit, merge the tree again, and the radius
   * chosen against Family's centre was replayed about the root's, throwing the
   * card better than a thousand units clear of the ring.
   */
  pinRingFor(ringId: string): string {
    const seen = new Set<string>();
    let cursor: string | null = ringId;
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      const slot: RingSlot | undefined = this.slots.get(cursor);
      if (!slot) break;
      if (slot.ownsRing) return slot.id;
      cursor = slot.placement.parentId;
    }
    return ROOT_RING_ID;
  }

  /**
   * Effective pin for an event on a ring: a live drag beats the stored pin, and
   * a per-ring pin beats the root one (docs/CIRCLES.md §7). The ring asked
   * about is the placement's, so a band falls through to its parent's pin and,
   * on the root, to the root pin or to auto.
   */
  pinFor(eventId: string, ringId: string = ROOT_RING_ID): FloatPin | null {
    const ring = this.pinRingFor(ringId);
    if (this.livePin?.eventId === eventId && this.livePin.ringId === ring) {
      return this.livePin.pin;
    }
    if (ring !== ROOT_RING_ID) {
      const onThisRing = this.pins.get(`${eventId}@${ring}`);
      if (onThisRing !== undefined) return onThisRing;
    }
    return this.pins.get(eventId) ?? null;
  }

  /**
   * Replace every effective pin at once (owner pins merged with overrides).
   * Keys are event ids for the root ring and `eventId@ringId` for any other.
   */
  setPins(pins: Map<string, FloatPin | null> | Record<string, FloatPin | null>): void {
    this.pins = pins instanceof Map ? new Map(pins) : new Map(Object.entries(pins));
    this.layoutPass(true);
  }

  setPin(eventId: string, pin: FloatPin | null, ringId: string = ROOT_RING_ID): void {
    const ring = this.pinRingFor(ringId);
    const key = ring === ROOT_RING_ID ? eventId : `${eventId}@${ring}`;
    if (pin) this.pins.set(key, pin);
    else this.pins.delete(key);
    this.layoutPass(true);
  }

  /**
   * Transient pin while a float is under the pointer. `null` ends the drag.
   * `ringId` keeps a drag on one circle's ring from dragging the same event's
   * other cards along with it.
   */
  setLivePin(eventId: string, pin: FloatPin | null, ringId: string = ROOT_RING_ID): void {
    this.livePin = pin ? { eventId, ringId: this.pinRingFor(ringId), pin } : undefined;
    this.layoutPass(true);
  }

  /** World geometry the layout algorithm needs at the current camera scale. */
  private layoutGeometry(): {
    gap: number;
    float: { width: number; height: number };
    cluster: { width: number; height: number };
    radius: number;
    maxSlide: number;
  } {
    const sizing = floatSizing(this.theme);
    const scale = this.view.scale > 0 ? this.view.scale : 1;
    const float = floatWorldSize(sizing.width, sizing.height, scale);
    const cluster = floatWorldSize(sizing.clusterWidth, sizing.clusterHeight, scale);
    // World-constant, so floats always clear the month label band — but never
    // thinner than `gapMinPx` on screen, which matters when zoomed out.
    const gap = Math.max(sizing.gap, sizing.gapMinPx / scale);
    const radius = RING_RADIUS + gap + float.height / 2;
    return {
      gap,
      float,
      cluster,
      radius,
      maxSlide: Math.max(
        MAX_SLIDE_FLOAT_WIDTHS * floatAngularExtent(float.width, radius),
        MIN_SLIDE_RAD,
      ),
    };
  }

  private currentArc(): Arc | null {
    const g = this.layoutGeometry();
    return visibleArc(this.view, this.viewport.width, this.viewport.height, [
      RING_RADIUS,
      RING_RADIUS + g.gap,
      RING_RADIUS + g.gap + g.float.height,
    ]);
  }

  /**
   * Every distinct (centre, ring line) that cards hang off.
   *
   * Cards anchor on the **ring line**, not on the band their event sits in: a
   * band is a tree ring a few percent of the radius deep, and threading the
   * leaders out from the band would fan the cards of one merged circle into
   * three near-identical rows for no gain. Anchoring them all at `r` is also
   * what keeps the outside of a merged calendar packed exactly as it was before
   * circles existed. Rings that genuinely sit apart — an orbit, a concentric
   * shell — still pack separately, which is what stops two of them packing
   * their cards through each other.
   */
  private floatRings(): FloatRing[] {
    const byKey = new Map<string, FloatRing>();
    for (const event of this.visible) {
      // A filtered-out entry is not packed at all, rather than packed and
      // hidden: leaving it in would have it go on pushing its neighbours
      // along, and the point of a search is the room it makes.
      if (!this.matchesFilter(event)) continue;
      const rings = this.eventRings.get(event.id);
      if (!rings) continue;
      for (const ringId of rings) {
        const slot = this.slots.get(ringId);
        if (!slot) continue;
        const key = `${slot.cx}|${slot.cy}|${slot.r}`;
        let ring = byKey.get(key);
        if (!ring) {
          ring = {
            key,
            cx: slot.cx,
            cy: slot.cy,
            radius: slot.r,
            primary: ringId,
            slotOf: new Map(),
            events: [],
          };
          byKey.set(key, ring);
        }
        // The root, when it is here at all, names the ring: it is the one whose
        // cluster ids the rest of the app already knows how to read.
        if (ringId === ROOT_RING_ID) ring.primary = ROOT_RING_ID;
        ring.slotOf.set(event.id, ringId);
        ring.events.push(event);
      }
    }
    return [...byKey.values()];
  }

  /**
   * Run the packer and reconcile the float/cluster/leader entries against it.
   * `force` skips the hysteresis; the scheduler uses it for data changes and
   * drags, where the answer must be current.
   */
  layoutPass(force = false): void {
    if (this.layoutPending) {
      cancelFrame(this.layoutPending);
      this.layoutPending = 0;
    }
    const g = this.layoutGeometry();
    const arc = this.currentArc();

    if (!force && !this.needsLayout(arc)) return;

    const placed: PlacedOnRing[] = [];
    for (const ring of this.floatRings()) {
      const items: LayoutItem[] = ring.events.map((event) => {
        const ringId = ring.slotOf.get(event.id) ?? ROOT_RING_ID;
        const slot = this.slots.get(ringId);
        const style = slot ? this.styleFor(event, slot) : undefined;
        const size = markSizeFactor(style?.prominence);
        return {
          id: event.id,
          angle: this.angleOf(event),
          width: g.float.width * size,
          height: g.float.height * size,
          pin: this.pinFor(event.id, ringId),
          // Prominence decides who keeps a card of their own when a run has to
          // give one up (docs/CIRCLES.md §4).
          priority: style?.prominence ?? 1,
        };
      });

      const maxSlide = Math.max(
        MAX_SLIDE_FLOAT_WIDTHS *
          floatAngularExtent(g.float.width, ring.radius + g.gap + g.float.height / 2),
        MIN_SLIDE_RAD,
      );

      // Only the ring at the origin can be culled against the visible arc: the
      // arc is measured about the world centre, and an orbiting ring is neither
      // there nor big enough for culling to be worth getting wrong.
      const centred = ring.cx === 0 && ring.cy === 0;
      const out = layoutFloats(items, {
        ringRadius: ring.radius,
        gap: g.gap,
        maxSlide,
        viewport: centred ? arc : null,
        clusterWidth: g.cluster.width,
        clusterHeight: g.cluster.height,
      });
      for (const p of out) placed.push({ ring, placed: p });
    }

    this.reconcile(placed, g);
    this.syncMarkScale();
    this.layoutScale = this.view.scale;
    this.layoutArc = arc;
    this.writeTransforms();
  }

  /**
   * Ring marks size themselves in screen pixels through `ctx.screenScale`, so
   * they need a rebuild when the zoom moves. Doing it here rather than in
   * `applyView` means at most once per layout pass — the same 5% hysteresis
   * that keeps clusters from flickering keeps marks from churning.
   */
  private syncMarkScale(): void {
    const screenScale = 1 / (this.view.scale > 0 ? this.view.scale : 1);
    for (const entry of this.entries.values()) {
      if (entry.kind !== 'ringMark') continue;
      if (entry.ctx.screenScale === screenScale) continue;
      entry.ctx = { ...entry.ctx, screenScale };
      this.rebuild(entry);
    }
  }

  private needsLayout(arc: Arc | null): boolean {
    if (this.layoutScale <= 0) return true;
    const zoomed = Math.abs(Math.log(this.view.scale / this.layoutScale));
    if (zoomed > LAYOUT_SCALE_HYSTERESIS) return true;
    return arcDrift(this.layoutArc, arc) > LAYOUT_ARC_HYSTERESIS;
  }

  /** Coalesce layout work onto the next frame; drags and data changes force it. */
  private scheduleLayout(): void {
    if (this.layoutPending) return;
    if (typeof requestAnimationFrame !== 'function') {
      this.layoutPass();
      return;
    }
    this.layoutPending = requestAnimationFrame(() => {
      this.layoutPending = 0;
      this.layoutPass();
    });
  }

  private reconcile(placed: PlacedOnRing[], g: ReturnType<Scene['layoutGeometry']>): void {
    const screenScale = 1 / (this.view.scale > 0 ? this.view.scale : 1);
    const wanted = new Set<string>();
    this.placements.clear();
    this.ownerOf.clear();
    this.ownersOfEvent.clear();

    const entryIdOf = (item: PlacedOnRing): string =>
      item.placed.kind === 'cluster'
        ? onRing(item.placed.id, item.ring.primary)
        : floatIdFor(item.placed.id, item.ring.slotOf.get(item.placed.id) ?? ROOT_RING_ID);

    for (const item of placed) {
      const id = entryIdOf(item);
      wanted.add(id);
      this.placements.set(id, item.placed);
      const members =
        item.placed.kind === 'cluster' ? item.placed.memberIds : [item.placed.id];
      for (const m of members) {
        const owners = this.ownersOfEvent.get(m);
        if (owners) owners.push(id);
        else this.ownersOfEvent.set(m, [id]);
        // The primary owner is the one on the event's first ring, which for
        // anything the root can see is the root: `ownerFor` has always meant
        // "the card you would select", and it still does.
        if (!this.ownerOf.has(m) || item.ring.primary === ROOT_RING_ID) {
          this.ownerOf.set(m, id);
        }
      }
    }

    const removed: string[] = [];
    for (const [id, entry] of [...this.entries]) {
      if (entry.kind !== 'float' && entry.kind !== 'cluster') continue;
      if (wanted.has(id)) continue;
      if (entry.state.selected || entry.state.hover) this.stashed.set(id, entry.state);
      else this.stashed.delete(id);
      entry.parent.remove();
      this.entries.delete(id);
      removed.push(id);
      const leaderId = leaderIdFor(id);
      const leader = this.entries.get(leaderId);
      if (leader) {
        leader.el.remove();
        this.entries.delete(leaderId);
        removed.push(leaderId);
      }
    }

    for (const item of placed) {
      const id = entryIdOf(item);
      if (item.placed.kind === 'cluster') this.syncCluster(id, item, screenScale);
      else this.syncFloat(id, item, screenScale);
      this.syncLeader(id, item, g);
    }

    if (removed.length) this.onEntriesChanged?.(removed);
  }

  private syncFloat(id: string, item: PlacedOnRing, screenScale: number): void {
    const p = item.placed as PlacedFloat;
    const event = this.visibleById.get(p.id);
    if (!event) return;
    const ringId = item.ring.slotOf.get(p.id) ?? ROOT_RING_ID;
    const slot = this.slots.get(ringId);
    const style = slot ? this.styleFor(event, slot) : undefined;
    const colour = style?.colour || undefined;
    const age = ageText(event, this.view.year);
    const orphaned = Boolean(event.orphanedAt);
    const anchor = offset(p.anchor, item.ring);
    const position = offset(p.position, item.ring);
    const sig = `float|${event.id}|${ringId}|${colour ?? ''}|${age ?? ''}|${orphaned}|${style?.prominence ?? 1}|${event.title}`;
    const ctx: FloatCtx = {
      event,
      anchor,
      position,
      ageText: age,
      screenScale,
      colour,
      style,
      ringId,
      source: this.sources.get(event.sourceId),
      orphaned,
    };

    const existing = this.entryOf('float', id);
    if (existing && existing.sig === sig) {
      // Position is not drawn by the fragment (the slot carries it), so moving
      // a float is a transform write, never a rebuild.
      existing.ctx = ctx;
      existing.world = position;
      if (existing.state.pinned !== p.pinned) this.setState(id, { pinned: p.pinned });
      return;
    }
    if (existing) {
      existing.ctx = ctx;
      existing.sig = sig;
      existing.world = position;
      existing.state = { ...existing.state, pinned: p.pinned };
      existing.parent.setAttribute('aria-label', this.labelFor(event));
      this.rebuild(existing);
      return;
    }

    const host = this.makeSlot(id, this.labelFor(event));
    const state = { ...(this.stashed.get(id) ?? EMPTY_STATE), pinned: p.pinned };
    this.add('float', id, ctx, state, host, event.id, ringId, position, [event.id], sig);
  }

  /**
   * A card's slot: where the scene puts it, and what a keyboard lands on.
   * `data-slot` names the entry inside, which is how `InputController` knows
   * what Enter was pressed on.
   */
  private makeSlot(entryId: string, label: string): SVGGElement {
    const host = svgEl('g', {
      class: 'float-slot',
      tabindex: 0,
      role: 'button',
      'data-slot': entryId,
      'aria-label': label,
    });
    this.floatSlots.append(host);
    return host;
  }

  private syncCluster(id: string, item: PlacedOnRing, screenScale: number): void {
    const p = item.placed as PlacedCluster;
    const members = p.memberIds
      .map((mid) => this.visibleById.get(mid))
      .filter((e): e is CalEvent => Boolean(e));
    if (members.length === 0) return;
    // Chronological, so the fan and the label read in date order.
    members.sort((a, b) => this.angleOf(a) - this.angleOf(b));

    const position = offset(p.position, item.ring);
    const label = clusterLabel(members.map((m) => m.title));
    const ctx: ClusterCtx = {
      memberEvents: members,
      label,
      anchor: offset(p.anchor, item.ring),
      position,
      hiddenCount: Math.max(0, members.length - CLUSTER_AVATARS),
      screenScale,
      ringId: item.ring.primary,
      colours: members.map((m) => this.colourOfOn(m, item.ring) ?? 'var(--accent)'),
    };
    const sig = `cluster|${id}`;

    const existing = this.entryOf('cluster', id);
    if (existing) {
      existing.ctx = ctx;
      existing.world = position;
      if (existing.sig !== sig) {
        existing.sig = sig;
        this.rebuild(existing);
      }
      return;
    }

    const host = this.makeSlot(id, `${label}: ${members.length} entries`);
    this.add(
      'cluster',
      id,
      ctx,
      { ...(this.stashed.get(id) ?? EMPTY_STATE), pinned: false },
      host,
      undefined,
      item.ring.primary,
      position,
      members.map((m) => m.id),
      sig,
    );
  }

  private syncLeader(
    ownerId: string,
    item: PlacedOnRing,
    g: ReturnType<Scene['layoutGeometry']>,
  ): void {
    const p = item.placed;
    const id = leaderIdFor(ownerId);
    const anchorAngle = xyToPolar(p.anchor.x, p.anchor.y).angle;
    const height = p.kind === 'cluster' ? g.cluster.height : g.float.height;
    // Attach to the card's near edge, radially. The card is upright rather than
    // radial, so off the 12 o'clock axis the last stretch tucks under it — which
    // is what makes the thread read as attached rather than as a stray line.
    const outward = p.radius >= item.ring.radius ? 1 : -1;
    const to = offset(polarToXY(p.angle, p.radius - outward * (height / 2)), item.ring);
    const bend = offset(polarToXY(anchorAngle, item.ring.radius + g.gap * 0.45), item.ring);

    const event = p.kind === 'float' ? this.visibleById.get(p.id) : undefined;
    const colour = event ? this.colourOfOn(event, item.ring) : undefined;
    const ctx: LeaderCtx = {
      from: offset(p.anchor, item.ring),
      to,
      bend,
      event,
      colour,
      ringId: item.ring.primary,
    };
    const pinned = p.kind === 'float' && p.pinned;

    const existing = this.entryOf('leader', id);
    if (existing) {
      const same =
        near(existing.ctx.to, to) &&
        near(existing.ctx.from, ctx.from) &&
        near(existing.ctx.bend, bend);
      existing.ctx = ctx;
      if (!same || existing.state.pinned !== pinned) {
        existing.state = { ...existing.state, pinned };
        this.rebuild(existing);
      }
      return;
    }
    const eventIds = p.kind === 'cluster' ? p.memberIds.slice() : [p.id];
    this.add(
      'leader',
      id,
      ctx,
      { ...(this.stashed.get(id) ?? EMPTY_STATE), pinned },
      this.leaderLayer,
      event?.id,
      item.ring.primary,
      undefined,
      eventIds,
    );
  }

  private colourOfOn(event: CalEvent, ring: FloatRing): string | undefined {
    const slot = this.slots.get(ring.slotOf.get(event.id) ?? ROOT_RING_ID);
    if (!slot) return undefined;
    return this.styleFor(event, slot).colour || undefined;
  }

  private rebuild(entry: SceneEntry): void {
    const el = this.build(entry);
    entry.parent.replaceChild(el, entry.el);
    entry.el = el;
  }

  // --- queries --------------------------------------------------------------

  entry(id: string): SceneEntry | undefined {
    return this.entries.get(id);
  }

  /** The entry, if it is there and is of `kind`; its `ctx` comes back typed. */
  entryOf<K extends FragmentKind>(kind: K, id: string): SceneEntryOf<K> | undefined {
    const entry = this.entries.get(id);
    return entry && entry.kind === kind ? (entry as SceneEntryOf<K>) : undefined;
  }

  ids(): IterableIterator<string> {
    return this.entries.keys();
  }

  all(): IterableIterator<SceneEntry> {
    return this.entries.values();
  }

  placement(id: string): Placed | undefined {
    return this.placements.get(id);
  }

  /** Entry id of the float or cluster currently showing an event. */
  ownerFor(eventId: string): string | undefined {
    return this.ownerOf.get(eventId);
  }

  /** ...and every card showing it, one per ring it is on. */
  ownersFor(eventId: string): string[] {
    return this.ownersOfEvent.get(eventId) ?? [];
  }

  /** The rings an event is drawn on, in layout order. */
  ringsFor(eventId: string): string[] {
    return this.eventRings.get(eventId) ?? [];
  }

  /** Every mark entry id for an event — one per ring it is on. */
  marksFor(eventId: string): string[] {
    return this.ringsFor(eventId)
      .map((ringId) => markIdFor(eventId, ringId))
      .filter((id) => this.entries.has(id));
  }

  eventById(eventId: string): CalEvent | undefined {
    return this.visibleById.get(eventId);
  }

  get visibleEventList(): CalEvent[] {
    return this.visible;
  }

  /**
   * Everything that highlights together with `id`: a mark, its float (or the
   * cluster that swallowed it), that card's leader, and — for a cluster — every
   * member's mark. Hover linkage and selection both walk this, and it crosses
   * rings: selecting one placement of an event lights up all of them, which is
   * how you see at a glance which circles a person is in.
   */
  linkedIds(id: string): string[] {
    const entry = this.entries.get(id);
    if (!entry) return [];
    const eventIds = entry.eventIds ?? (entry.eventId ? [entry.eventId] : []);
    if (eventIds.length === 0) return [id];

    const out = new Set<string>([id]);
    const owners = new Set<string>();
    for (const eventId of eventIds) {
      for (const markId of this.marksFor(eventId)) out.add(markId);
      for (const owner of this.ownersFor(eventId)) owners.add(owner);
    }
    for (const owner of owners) {
      out.add(owner);
      const placed = this.placements.get(owner);
      if (placed?.kind === 'cluster') {
        for (const m of placed.memberIds) for (const markId of this.marksFor(m)) out.add(markId);
      }
      const leaderId = leaderIdFor(owner);
      if (this.entries.has(leaderId)) out.add(leaderId);
    }
    return [...out];
  }

  // --- state ----------------------------------------------------------------

  /** Re-render exactly one fragment when its state changes. */
  setState(id: string, patch: Partial<FragState>): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    const next = { ...entry.state, ...patch };
    if (
      next.hover === entry.state.hover &&
      next.selected === entry.state.selected &&
      next.dimmed === entry.state.dimmed &&
      next.pinned === entry.state.pinned
    ) {
      return;
    }
    entry.state = next;
    this.rebuild(entry);
    if (entry.kind === 'float' || entry.kind === 'cluster') {
      this.placeSlot(entry);
      // SVG paints in document order, so the card under the pointer moves to
      // the end of its layer: its hover preview must not sit under a neighbour.
      if (next.hover && !entry.state.selected) {
        const slot = entry.el.closest('.float-slot');
        if (slot && slot.parentNode === this.floatSlots) this.floatSlots.append(slot);
      }
    }
  }

  /**
   * Tell the month fragments where the ring has come to rest.
   *
   * A month arc lives in world coordinates and turns with the ring, but its
   * label is read on screen: a month rotated from the bottom of the ring to the
   * top needs its text path reversed or it reads mirrored. Only the fragments
   * whose readable orientation actually changed are rebuilt, and only when the
   * rotation settles — after inertia, a snap, or a bring-to-top animation —
   * never per frame, so spinning stays two transform writes.
   */
  syncRotation(rotation: number = this.view.rotation): void {
    for (const entry of this.entries.values()) {
      if (entry.kind !== 'month') continue;
      const previous = entry.ctx.rotation ?? 0;
      if (previous === rotation) continue;
      const mid = (entry.ctx.startAngle + entry.ctx.endAngle) / 2;
      const flipped = readsMirrored(mid, rotation);
      entry.ctx = { ...entry.ctx, rotation };
      if (flipped !== readsMirrored(mid, previous)) this.rebuild(entry);
    }
  }

  // --- transforms (per frame; no re-render) ---------------------------------

  applyView(view: ViewState): void {
    const scaleChanged = view.scale !== this.view.scale;
    this.view = view;
    this.writeTransforms();

    if (scaleChanged) {
      // screenScale is part of the float context; keep it truthful even between
      // layout passes.
      const screenScale = 1 / (view.scale > 0 ? view.scale : 1);
      for (const entry of this.entries.values()) {
        if (entry.kind === 'float' || entry.kind === 'cluster') {
          entry.ctx = { ...entry.ctx, screenScale };
        }
      }
      // Crossing the tree-ring threshold changes what the rings *are*, not just
      // where they sit, so it is the one zoom that costs a rebuild.
      if (this.bundle && !this.tweening && this.bandsShouldCollapse() !== this.bandsCollapsed) {
        this.render(this.bundle, view, this.today);
        return;
      }
    }
    if (this.needsLayout(this.currentArc())) this.scheduleLayout();
  }

  private writeTransforms(): void {
    const view = this.view;
    this.camera.setAttribute(
      'transform',
      `translate(${view.tx} ${view.ty}) scale(${view.scale})`,
    );
    const deg = view.rotation * DEG;
    this.ringLayer.setAttribute('transform', `rotate(${deg})`);
    this.leaderLayer.setAttribute('transform', `rotate(${deg})`);
    for (const entry of this.entries.values()) {
      if (entry.kind === 'float' || entry.kind === 'cluster') this.placeSlot(entry);
    }
  }

  private placeSlot(entry: SceneEntry): void {
    if (!entry.world) return;
    const p = rotatePoint(entry.world, this.view.rotation);
    entry.parent.setAttribute(
      'transform',
      `translate(${p.x.toFixed(2)} ${p.y.toFixed(2)}) scale(${(1 / this.view.scale).toFixed(4)})`,
    );
  }

  get currentView(): ViewState {
    return this.view;
  }

  get currentTheme(): Theme {
    return this.theme;
  }

  get currentToday(): Date {
    return this.today;
  }

  get currentViewport(): { width: number; height: number } {
    return this.viewport;
  }
}

/** One distinct (centre, ring line) that cards hang off; see `Scene.floatRings`. */
interface FloatRing {
  key: string;
  cx: number;
  cy: number;
  /** The ring line the cards anchor on — not the band radius the marks use. */
  radius: number;
  /** The ring whose id names this one's clusters. */
  primary: string;
  /** eventId → the ring it actually belongs to within this float ring. */
  slotOf: Map<string, string>;
  events: CalEvent[];
}

interface PlacedOnRing {
  ring: FloatRing;
  placed: Placed;
}

/** Ring-local float coordinates into absolute world ones. */
function offset(point: Point, ring: FloatRing): Point {
  return ring.cx === 0 && ring.cy === 0
    ? point
    : { x: point.x + ring.cx, y: point.y + ring.cy };
}

function setRingScale(group: SVGGElement, k: number): void {
  if (k === 1) group.removeAttribute('transform');
  else group.setAttribute('transform', `scale(${k.toFixed(5)})`);
}

/**
 * The opacity a fragment is drawn at: the cascade's own `opacity` token times
 * the prominence floor from §4, which is 1 at the default weight. Both being
 * neutral means no attribute at all, so nothing is written onto a calendar that
 * has never heard of circles.
 */
function entryOpacity(entry: SceneEntry): string | undefined {
  if (entry.kind !== 'ringMark' && entry.kind !== 'float') return undefined;
  const style = entry.ctx.style;
  if (!style) return undefined;
  const value = clamp01(style.opacity) * opacityFloor(style.prominence);
  return value >= 1 ? undefined : value.toFixed(3);
}

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 1;
}

function cancelFrame(handle: number): void {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle);
}

/** `float:evt1` → `leader:evt1`; `cluster:a,b` → `leader:cluster:a,b`. */
export function leaderIdFor(entryId: string): string {
  return entryId.startsWith('float:') ? `leader:${entryId.slice(6)}` : `leader:${entryId}`;
}

function near(a: Point | undefined, b: Point): boolean {
  return Boolean(a) && Math.abs(a!.x - b.x) < 0.05 && Math.abs(a!.y - b.y) < 0.05;
}

function sameRecord(a: Record<string, string>, b: Record<string, string>): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => a[key] === b[key]);
}

/**
 * Notes pinned to a single year only show in that year; everything else recurs.
 * Orphaned events (upstream-deleted) stay on the ring, drawn faded.
 */
export function visibleEvents(events: CalEvent[], year: number): CalEvent[] {
  return events.filter((e) => {
    if (e.kind === 'note' && e.year !== undefined && e.year !== year) return false;
    return true;
  });
}
